import { useQuery } from '@tanstack/react-query';
import { CreditCard, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BILLING_OVERVIEW_KEY, billingApi } from '@/lib/billing-api';
import { getErrorMessage } from '@/lib/errors';

const PADDLE_JS = 'https://cdn.paddle.com/paddle/v2/paddle.js';

/** Only the surface this page uses. Paddle.js has no types package worth pulling in for three calls. */
interface PaddleGlobal {
  Environment: { set(environment: string): void };
  Initialize(options: {
    token: string;
    eventCallback?: (event: { name?: string; data?: { custom_data?: unknown } }) => void;
  }): void;
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

/** Loads Paddle.js once per document, and resolves on the load event rather than on a timer. */
function loadPaddleJs(): Promise<void> {
  if (window.Paddle !== undefined) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${PADDLE_JS}"]`);
    const script = existing ?? document.createElement('script');

    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () =>
      // The realistic cause by far. Naming it is the difference between a customer disabling a
      // blocker and a customer concluding the platform is broken.
      reject(new Error('Could not load the payment provider’s script. An ad blocker or privacy '
        + 'extension is the usual cause. Allow cdn.paddle.com and reload this page.')),
    );

    if (existing === null) {
      script.src = PADDLE_JS;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

/** Reads our subscription id back out of the transaction's `custom_data`. */
function subscriptionIdFrom(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  const value = (data as Record<string, unknown>).subscription_id;

  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * The page Paddle's payment link points at.
 *
 * ## Why a page of ours is involved at all
 *
 * Paddle Billing does not host a checkout you can simply link to. Creating a transaction returns
 * `<your default payment link>?_ptxn=<transaction id>`, and that default payment link must be
 * "a page for an approved website that includes Paddle.js" — a page on *this* platform. Paddle.js
 * sees `_ptxn` in the URL and opens the checkout overlay itself; nothing here has to ask it to.
 *
 * Without this page the flow dead-ends. The backend creates a real transaction, redirects the
 * customer to whatever the default payment link happens to be, and if that is not a page running
 * Paddle.js then nothing opens — the customer has expressed an intent to pay and been handed a
 * blank wall. That was the state this platform was in.
 *
 * ## Set this URL in Paddle
 *
 * Paddle > Checkout > Checkout settings > Default payment link, pointing at `/billing/pay` on this
 * installation's own domain. Sandbox and live are separate systems and each needs its own.
 *
 * ## Returning the customer afterwards
 *
 * Paddle.js emits `checkout.completed`, carrying back the `custom_data` the transaction was created
 * with — which is where {@see CheckoutRequest::correlationMetadata()} put our subscription id. That
 * is the whole handoff to the confirmation page. It is deliberately not treated as proof of
 * anything: the confirmation page still waits for the webhook before telling anybody they are
 * subscribed. This event just says which subscription to watch.
 */
export function CheckoutPaymentPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const transaction = params.get('_ptxn') ?? '';
  const [failure, setFailure] = useState<string | null>(null);
  const initialised = useRef(false);

  const overview = useQuery({ queryKey: BILLING_OVERVIEW_KEY, queryFn: billingApi.overview });

  const paddle = overview.data?.available_gateways.find((g) => g.identifier === 'paddle');
  const token = paddle?.client_token ?? '';

  useEffect(() => {
    // React 18's development double-invoke would otherwise initialise Paddle.js twice and open two
    // overlays over each other.
    if (token === '' || transaction === '' || initialised.current) {
      return;
    }

    initialised.current = true;

    void loadPaddleJs()
      .then(() => {
        const sdk = window.Paddle;

        if (sdk === undefined) {
          throw new Error('The payment provider’s script loaded but did not start.');
        }

        // Before Initialize, and only for sandbox: the SDK defaults to production, and a sandbox
        // token pointed at production fails with nothing useful on screen.
        if (paddle?.client_environment === 'sandbox') {
          sdk.Environment.set('sandbox');
        }

        sdk.Initialize({
          token,
          eventCallback: (event) => {
            if (event.name !== 'checkout.completed') {
              return;
            }

            const subscriptionId = subscriptionIdFrom(event.data?.custom_data);

            // No id means we cannot point the confirmation page at anything, so send them to
            // billing, which reads the real state rather than guessing at it.
            navigate(
              subscriptionId === null
                ? '/billing'
                : `/billing/checkout/success?subscription=${encodeURIComponent(subscriptionId)}`,
            );
          },
        });
      })
      .catch((err: unknown) => setFailure(getErrorMessage(err)));
  }, [navigate, paddle?.client_environment, token, transaction]);

  const missingTransaction = transaction === '';
  const missingToken = overview.isSuccess && token === '';

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-10">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="size-5" />
            Payment
          </CardTitle>
          <CardDescription>
            The payment form opens over this page. Your card details are entered on the payment
            provider’s form and never reach this platform.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {overview.isError && <Alert variant="danger">{getErrorMessage(overview.error)}</Alert>}
          {failure !== null && <Alert variant="danger">{failure}</Alert>}

          {/* Somebody reached this page without a transaction — a bookmark, or the payment link
              opened by hand. There is nothing to pay for, and saying so is better than an empty
              page waiting for an overlay that will never arrive. */}
          {missingTransaction && (
            <Alert variant="info">
              This page opens a payment that has already been started. Choose a plan on the billing
              page to begin one.
            </Alert>
          )}

          {missingToken && !missingTransaction && (
            <Alert variant="danger">
              Card payments are not fully configured on this installation, so the payment form
              cannot open. Please contact support. You have not been charged.
            </Alert>
          )}

          {!missingTransaction && !missingToken && failure === null && (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" />
              Opening the secure payment form…
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button asChild variant="outline">
              <Link to="/billing">Back to billing</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
